import { createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";

import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { NamedIcon } from "~/components/ui/named-icon";
import { BRAND } from "~/lib/shared/brand";
import { createBrandMeta } from "~/lib/shared/meta";
import { gray, orange, purple, violet } from "~/lib/ui";

export const Route = createFileRoute("/_public/brand/guideline/")({
  component: BrandGuidelinesPage,
  staticData: { title: "Guideline" },
  head: () =>
    createBrandMeta({
      title: "Brand Guideline | Dream Coach Match",
      description: "Brand guideline and design system for Dream Coach Match",
      noindex: true,
    }),
});

function BrandGuidelinesPage() {
  return (
    <main className="min-h-screen bg-gradient-to-b from-gray-25 via-white to-gray-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-gray-200 bg-gradient-to-br from-purple-50 via-violet-50 to-purple-50">
        <div className="bg-grid-pattern absolute inset-0 opacity-5" />
        <div className="relative mx-auto max-w-screen-xl px-4 py-24 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-100/50 px-4 py-2 text-sm font-medium text-purple-700">
              <NamedIcon name="palette" className="size-4" />
              Brand Guideline
            </div>
            <h1 className="mb-6 font-heading text-5xl font-medium tracking-tight text-gray-900 md:text-6xl lg:text-7xl">
              Dream Coach Match
            </h1>
            <p className="mx-auto mb-8 max-w-2xl text-xl text-muted-foreground md:text-2xl">
              {BRAND.slogan}
            </p>
            <p className="mx-auto max-w-3xl text-lg text-muted-foreground">
              Our brand represents the connection between coaches and clients,
              built on trust, transformation, and exceptional matching. These
              guidelines ensure consistency across all touchpoints.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="mx-auto max-w-screen-xl px-4 py-16 md:py-24">
        {/* Brand Identity */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-24"
        >
          <div className="mb-12">
            <h2 className="mb-4 font-heading text-3xl font-medium tracking-tight text-gray-900 md:text-4xl">
              Brand Identity
            </h2>
            <p className="text-lg text-muted-foreground">
              Dream Coach Match is a revolutionary platform that connects
              clients with their ideal coaches through sophisticated AI-powered
              matching. We believe in transformation, trust, and creating
              meaningful connections.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100/50">
              <CardHeader>
                <NamedIcon
                  name="compass"
                  className="mb-2 size-8 text-purple-600"
                />
                <CardTitle>Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Connect clients with the perfect coach through intelligent
                  matching and exceptional service.
                </CardDescription>
              </CardContent>
            </Card>
            <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-violet-100/50">
              <CardHeader>
                <NamedIcon
                  name="target"
                  className="mb-2 size-8 text-violet-600"
                />
                <CardTitle>Values</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Trust, transformation, and transparency guide everything we
                  do.
                </CardDescription>
              </CardContent>
            </Card>
            <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-orange-100/50">
              <CardHeader>
                <NamedIcon
                  name="rocket"
                  className="mb-2 size-8 text-orange-600"
                />
                <CardTitle>Vision</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  To become the world's most trusted coaching marketplace.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </motion.section>

        {/* Color Palette */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-24"
        >
          <div className="mb-12">
            <h2 className="mb-4 font-heading text-3xl font-medium tracking-tight text-gray-900 md:text-4xl">
              Color Palette
            </h2>
            <p className="text-lg text-muted-foreground">
              Our color system is built around purple as the primary brand
              color, with violet and orange as accents. These colors convey
              trust, transformation, and energy.
            </p>
          </div>

          {/* Primary Brand Colors */}
          <div className="mb-12 space-y-8">
            <div>
              <h3 className="mb-4 font-heading text-2xl font-medium text-gray-900">
                Primary Colors
              </h3>
              <p className="text-base text-muted-foreground">
                Purple anchors every brand touchpoint. Purple 700 is the
                definitive Dream Coach Match color—use it for primary actions,
                navigation highlights, and any element that should unmistakably
                feel like us.
              </p>
            </div>
            <div className="max-w-lg">
              <ColorSwatch
                name="Purple 700"
                hex={purple[700]}
                description="Primary brand color"
                usage="Core brand surfaces, primary buttons, key gradients"
              />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-heading text-lg font-medium text-gray-900">
                  Accent distribution
                </h4>
                <p className="text-sm text-muted-foreground">
                  Violet supports purple. Orange is for rare emphasis.
                </p>
              </div>
              <ColorWeightDistribution
                colors={[
                  {
                    name: "Purple core",
                    hex: purple[700],
                    weight: 7,
                    description: "70% — default state for actions and key UI",
                  },
                  {
                    name: "Violet accent",
                    hex: violet[500],
                    weight: 2,
                    description:
                      "20% — subtle highlights, secondary buttons, charts",
                  },
                  {
                    name: "Orange highlight",
                    hex: orange[500],
                    weight: 1,
                    description:
                      "10% — promotional callouts and urgent badges only",
                  },
                ]}
              />
            </div>
          </div>

          {/* Full Color Scales */}
          <div className="mb-8 space-y-4">
            <h4 className="font-heading text-xl font-medium text-gray-900">
              Using the scales
            </h4>
            <p className="text-base text-muted-foreground">
              Use 50–200 shades for backgrounds and large surfaces, 300–500 for
              secondary components, and 600+ for text, icons, and emphasis. Keep
              contrast above WCAG AA for accessibility.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            <ColorScale name="Purple" colors={purple} />
            <ColorScale name="Violet" colors={violet} />
            <ColorScale name="Orange" colors={orange} />
            <ColorScale name="Gray" colors={gray} />
          </div>

          <div className="mt-12 space-y-6">
            <h4 className="font-heading text-xl font-medium text-gray-900">
              Key color guidance
            </h4>
            <div className="grid gap-6 md:grid-cols-3">
              <ColorUsageCard
                name="Backgrounds"
                hex={gray[25]}
                description="Use Gray 25 as the default page background and Gray 50 for subtle section breaks."
                guidance="Avoid pure white; the warm tint keeps the interface soft and premium."
              />
              <ColorUsageCard
                name="Content surfaces"
                hex="#FFFFFF"
                description="Layer cards and modals on white or Gray 50 with Purple 50 borders when needed."
                guidance="Ensure 16px padding minimum so surfaces feel airy."
              />
              <ColorUsageCard
                name="Text & icons"
                hex={gray[900]}
                description="Primary text is Gray 900 on light backgrounds, with Gray 600 for supporting copy."
                guidance="Reserve Purple 700 text for links or actionable elements only."
              />
            </div>
          </div>
        </motion.section>

        {/* Design Tokens */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-24"
        >
          <div className="mb-12">
            <h2 className="mb-4 font-heading text-3xl font-medium tracking-tight text-gray-900 md:text-4xl">
              Design Tokens
            </h2>
            <p className="text-lg text-muted-foreground">
              Purple is reserved for primary actions and brand moments. Most UI
              chrome — focus rings, secondary buttons, accent surfaces — uses
              neutral tokens. This "purple restraint" strategy keeps the brand
              punchy without overwhelming the interface.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Card variant="bordered">
              <CardHeader>
                <NamedIcon name="focus" className="mb-2 size-6 text-gray-500" />
                <CardTitle className="text-lg">--color-ring</CardTitle>
                <CardDescription>
                  Focus ring color. Set to{" "}
                  <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs">
                    gray-300
                  </code>{" "}
                  for neutral focus states instead of purple.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <span className="inline-flex size-8 rounded-full border-2 border-gray-300 bg-white" />
                  <span className="text-sm text-muted-foreground">
                    Neutral focus — keeps attention on content, not chrome
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card variant="bordered">
              <CardHeader>
                <NamedIcon
                  name="layers"
                  className="mb-2 size-6 text-gray-500"
                />
                <CardTitle className="text-lg">
                  --color-surface-accent
                </CardTitle>
                <CardDescription>
                  Subtle accent background. Set to{" "}
                  <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs">
                    gray-50
                  </code>{" "}
                  to replace ad-hoc brand-50 tinted surfaces.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <span className="inline-flex size-8 rounded border border-gray-200 bg-gray-50" />
                  <span className="text-sm text-muted-foreground">
                    Neutral surface for highlighted areas and accent regions
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card variant="bordered">
              <CardHeader>
                <NamedIcon
                  name="square"
                  className="mb-2 size-6 text-gray-500"
                />
                <CardTitle className="text-lg">--color-secondary</CardTitle>
                <CardDescription>
                  Secondary button / badge background. Now{" "}
                  <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs">
                    gray-100
                  </code>{" "}
                  instead of brand-50.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <span className="inline-flex size-8 rounded border border-gray-200 bg-gray-100" />
                  <span className="text-sm text-muted-foreground">
                    Paired with{" "}
                    <code className="font-mono text-xs">
                      --color-secondary-foreground
                    </code>{" "}
                    (gray-800)
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card variant="bordered">
              <CardHeader>
                <NamedIcon
                  name="settings"
                  className="mb-2 size-6 text-gray-500"
                />
                <CardTitle className="text-lg">Tuning the balance</CardTitle>
                <CardDescription>
                  All tokens live in{" "}
                  <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs">
                    globals.css @theme
                  </code>
                  . Adjusting them shifts the purple balance globally — no
                  component changes needed.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  To increase brand presence, swap any neutral token back toward
                  a purple shade. To reduce it, move further into the gray
                  scale.
                </p>
              </CardContent>
            </Card>
          </div>
        </motion.section>

        {/* Typography */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-24"
        >
          <div className="mb-12">
            <h2 className="mb-4 font-heading text-3xl font-medium tracking-tight text-gray-900 md:text-4xl">
              Typography
            </h2>
            <p className="text-lg text-muted-foreground">
              We use DM Sans for headings and Inter for body text, creating a
              modern, readable, and approachable typographic system.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Headings — DM Sans</CardTitle>
                <CardDescription>
                  Used for all headings, providing a distinctive and friendly
                  character.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-heading text-4xl font-medium tracking-tight text-gray-900">
                    Heading 1
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Medium, 2.25rem (36px)
                  </p>
                </div>
                <div>
                  <p className="font-heading text-3xl font-medium tracking-tight text-gray-900">
                    Heading 2
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Medium, 1.875rem (30px)
                  </p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-medium tracking-tight text-gray-900">
                    Heading 3
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Medium, 1.5rem (24px)
                  </p>
                </div>
                <div>
                  <p className="font-heading text-xl font-medium text-gray-900">
                    Heading 4
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Medium, 1.25rem (20px)
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Body — Inter</CardTitle>
                <CardDescription>
                  Used for all body text, ensuring excellent readability across
                  devices.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-lg text-gray-900">
                    Large body text (18px) — Perfect for introductory paragraphs
                    and important content.
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Regular, 1.125rem (18px)
                  </p>
                </div>
                <div>
                  <p className="text-base text-gray-900">
                    Regular body text (16px) — The standard size for most
                    content.
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Regular, 1rem (16px)
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-900">
                    Small text (14px) — Used for captions, labels, and secondary
                    information.
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Regular, 0.875rem (14px)
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.section>

        {/* Logo Usage */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mb-24"
        >
          <div className="mb-12">
            <h2 className="mb-4 font-heading text-3xl font-medium tracking-tight text-gray-900 md:text-4xl">
              Logo Usage
            </h2>
            <p className="text-lg text-muted-foreground">
              Our logo represents the Dream Coach Match brand. Follow these
              guidelines to maintain brand consistency.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Do's</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Use sufficient clear space around the logo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Maintain proper aspect ratio</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Use approved color variations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Ensure minimum size requirements</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Don'ts</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-red-600">✗</span>
                    <span>Stretch, skew, or distort the logo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600">✗</span>
                    <span>Change colors arbitrarily</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600">✗</span>
                    <span>Place on busy backgrounds</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600">✗</span>
                    <span>Use outdated logo versions</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </motion.section>

        {/* Components */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mb-24"
        >
          <div className="mb-12">
            <h2 className="mb-4 font-heading text-3xl font-medium tracking-tight text-gray-900 md:text-4xl">
              Components
            </h2>
            <p className="text-lg text-muted-foreground">
              Our component library provides consistent, accessible, and
              beautiful UI elements. Cards use a shadow-first approach — no
              border by default, just elevation.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Buttons</CardTitle>
                <CardDescription>
                  Primary actions use our brand purple gradient, with various
                  styles for different contexts. Secondary buttons use semantic
                  tokens (
                  <code className="font-mono text-xs">bg-secondary</code> /{" "}
                  <code className="font-mono text-xs">
                    text-secondary-foreground
                  </code>
                  ) so their appearance updates globally via design tokens.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-4">
                <Button>Primary Button</Button>
                <Button>Default</Button>
                <Button variant="primary">Primary</Button>
                <Button variant="ghost">Ghost</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Cards</CardTitle>
                <CardDescription>
                  Cards follow a shadow-first philosophy. The default variant
                  uses shadow-sm with hover:shadow-md and no border. Use the
                  bordered variant when an explicit edge is needed, and flat for
                  inline or nested cards.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                    Default (shadow only)
                  </p>
                  <Card>
                    <CardContent className="py-3 text-sm text-gray-700">
                      No border — elevation via shadow
                    </CardContent>
                  </Card>
                </div>
                <div>
                  <p className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                    Bordered
                  </p>
                  <Card variant="bordered">
                    <CardContent className="py-3 text-sm text-gray-700">
                      Explicit border + shadow
                    </CardContent>
                  </Card>
                </div>
                <div>
                  <p className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                    Flat
                  </p>
                  <Card variant="flat">
                    <CardContent className="py-3 text-sm text-gray-700">
                      No shadow, no border — for nested or inline use
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.section>

        {/* Spacing & Layout */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mb-24"
        >
          <div className="mb-12">
            <h2 className="mb-4 font-heading text-3xl font-medium tracking-tight text-gray-900 md:text-4xl">
              Spacing & Layout
            </h2>
            <p className="text-lg text-muted-foreground">
              Consistent spacing creates visual harmony and improves readability
              across the platform.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Spacing Scale</CardTitle>
              <CardDescription>
                We use a 4px base unit for consistent spacing throughout the
                application.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[4, 8, 12, 16, 24, 32, 48, 64].map((size) => (
                  <div key={size} className="flex items-center gap-4">
                    <div
                      className="rounded bg-purple-600"
                      style={{ width: size, height: size }}
                    />
                    <div className="flex-1 border-t border-dashed border-gray-300" />
                    <span className="font-mono text-sm text-muted-foreground">
                      {size}px
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.section>

        {/* Navigation & Layout */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mb-24"
        >
          <div className="mb-12">
            <h2 className="mb-4 font-heading text-3xl font-medium tracking-tight text-gray-900 md:text-4xl">
              Navigation & Layout
            </h2>
            <p className="text-lg text-muted-foreground">
              The platform uses three distinct layout contexts, each tailored to
              its audience and embedding constraints.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <Card variant="bordered">
              <CardHeader>
                <NamedIcon
                  name="panel-left"
                  className="mb-2 size-8 text-purple-600"
                />
                <CardTitle>Authenticated App</CardTitle>
                <CardDescription>User side</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-700">
                  Sidebar-based app shell using shadcn Sidebar in{" "}
                  <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs">
                    collapsible=&quot;icon&quot;
                  </code>{" "}
                  mode.
                </p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>SidebarProvider + AppSidebar</li>
                  <li>SidebarInset + AppTopbar</li>
                  <li>max-w-6xl content area</li>
                </ul>
              </CardContent>
            </Card>

            <Card variant="bordered">
              <CardHeader>
                <NamedIcon
                  name="globe"
                  className="mb-2 size-8 text-violet-600"
                />
                <CardTitle>Public Pages</CardTitle>
                <CardDescription>Marketing & discovery</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-700">
                  Traditional header + footer layout with full-width content
                  areas for landing pages and the discovery flow.
                </p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>Shared header with navigation</li>
                  <li>Full-width hero sections</li>
                  <li>Footer with links and branding</li>
                </ul>
              </CardContent>
            </Card>

            <Card variant="bordered">
              <CardHeader>
                <NamedIcon
                  name="app-window"
                  className="mb-2 size-8 text-orange-600"
                />
                <CardTitle>Coach Backend</CardTitle>
                <CardDescription>GoHighLevel embed</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-700">
                  Embedded layout within the GoHighLevel iframe. No header or
                  footer — the host platform provides chrome.
                </p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>Iframe-based auth via locationId</li>
                  <li>Minimal standalone layout</li>
                  <li>Host-provided navigation</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </motion.section>

        {/* Voice & Tone */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="mb-24"
        >
          <div className="mb-12">
            <h2 className="mb-4 font-heading text-3xl font-medium tracking-tight text-gray-900 md:text-4xl">
              Voice & Tone
            </h2>
            <p className="text-lg text-muted-foreground">
              Our communication should be clear, supportive, and
              inspiring—reflecting the transformative journey we facilitate.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <Card className="border-purple-200">
              <CardHeader>
                <CardTitle>Clear</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Use straightforward language that everyone can understand.
                  Avoid jargon unless necessary.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-violet-200">
              <CardHeader>
                <CardTitle>Supportive</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Be encouraging and empathetic. Recognize that finding the
                  right coach is a personal journey.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-orange-200">
              <CardHeader>
                <CardTitle>Inspiring</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Celebrate transformation and growth. Help people envision
                  their potential and possibilities.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </motion.section>
      </div>
    </main>
  );
}

interface ColorSwatchProps {
  name: string;
  hex: string;
  description: string;
  usage: string;
}

function ColorSwatch({ name, hex, description, usage }: ColorSwatchProps) {
  return (
    <Card className="overflow-hidden border-gray-200">
      <div className="h-32 w-full" style={{ backgroundColor: hex }} />
      <CardHeader>
        <CardTitle className="text-lg">{name}</CardTitle>
        <CardDescription className="font-mono text-xs">{hex}</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="mb-2 text-sm text-muted-foreground">{description}</p>
        <p className="text-xs text-muted-foreground italic">{usage}</p>
      </CardContent>
    </Card>
  );
}

interface ColorScaleProps {
  name: string;
  colors: Record<string, string>;
}

function ColorScale({ name, colors }: ColorScaleProps) {
  const colorEntries = Object.entries(colors).sort(([a], [b]) => {
    const numA = Number.parseInt(a, 10);
    const numB = Number.parseInt(b, 10);
    return numA - numB;
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{name} Scale</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-5 gap-2">
          {colorEntries.map(([key, hex]) => (
            <div key={key} className="text-center">
              <div
                className="mb-2 h-16 w-full rounded-lg border border-gray-200"
                style={{ backgroundColor: hex }}
              />
              <p className="mb-1 text-xs font-semibold text-muted-foreground">
                {key}
              </p>
              <p className="font-mono text-xs text-muted-foreground">{hex}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

interface ColorWeightSpec {
  name: string;
  hex: string;
  weight: number;
  description: string;
}

interface ColorWeightDistributionProps {
  colors: readonly ColorWeightSpec[];
}

function ColorWeightDistribution({ colors }: ColorWeightDistributionProps) {
  const totalWeight = colors.reduce((sum, color) => sum + color.weight, 0);

  return (
    <div className="space-y-3">
      <div className="flex h-12 overflow-hidden rounded-lg border border-gray-200">
        {colors.map((color) => {
          const percent = Math.round((color.weight / totalWeight) * 100);
          return (
            <div
              key={color.name}
              aria-label={`${color.name} ${percent}%`}
              className="relative"
              style={{ backgroundColor: color.hex, flex: color.weight }}
            >
              <span className="sr-only">{`${color.name} ${percent}%`}</span>
            </div>
          );
        })}
      </div>
      <dl className="grid gap-4 text-sm text-gray-700 md:grid-cols-3">
        {colors.map((color) => (
          <div key={color.name} className="space-y-1">
            <dt className="font-semibold">{color.name}</dt>
            <dd className="text-sm text-muted-foreground">
              {color.description}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

interface ColorUsageCardProps {
  name: string;
  hex: string;
  description: string;
  guidance: string;
}

function ColorUsageCard({
  name,
  hex,
  description,
  guidance,
}: ColorUsageCardProps) {
  return (
    <Card className="border-gray-200">
      <CardHeader className="space-y-3">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="inline-flex size-6 rounded-full border border-gray-200"
            style={{ backgroundColor: hex }}
          />
          <CardTitle className="text-lg">{name}</CardTitle>
        </div>
        <CardDescription className="text-sm">{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm leading-relaxed text-muted-foreground">
          {guidance}
        </p>
        <p className="font-mono text-xs tracking-wide text-muted-foreground uppercase">
          {hex}
        </p>
      </CardContent>
    </Card>
  );
}
